/**
 * CanvasThumbnail - Static canvas thumbnail
 *
 * Supports two data sources:
 * - blockId: For block-backed content (plays, formations, concepts)
 * - players: For legacy formation table data (Player[] → rendered without API call)
 */

import { useMemo } from 'react'
import { CanvasRenderer } from './CanvasRenderer'
import { playersToPlayerBlocks } from '@/adapters/PlayerAdapter'
import { THUMBNAIL_VIEW_BOX } from '@/constants'
import { DEFAULT_CANVAS_LAYOUT } from '@/types/blocks/shared/Layout'
import type { FormationBlock, ResolvedBlock } from '@/types/blocks'
import type { Player } from '@/types/Player.types'

interface CanvasThumbnailProps {
	blockId?: string
	players?: Player[]
	showField?: boolean
	className?: string
}

const THUMBNAIL_SCALE = {
	stroke: 0.5,
	playerFont: 1.3,
}

export function CanvasThumbnail({
	blockId,
	players,
	showField = true,
	className,
}: CanvasThumbnailProps) {
	// Convert Player[] to a synthetic ResolvedBlock for rendering
	const resolvedBlock = useMemo<ResolvedBlock | undefined>(() => {
		if (!players) return undefined

		const syntheticBlock: FormationBlock = {
			id: 'formation-thumbnail',
			type: 'formation',
			parent_block_id: null,
			root_block_id: 'formation-thumbnail',
			content: [],
			created_by: '00000000-0000-0000-0000-000000000000',
			created_at: new Date(),
			updated_at: new Date(),
			properties: { name: 'Formation', hashMode: false },
			...DEFAULT_CANVAS_LAYOUT,
		}

		return {
			block: syntheticBlock,
			players: playersToPlayerBlocks(players),
			drawings: [],
			annotations: [],
			formationBlock: null,
			conceptReferences: [],
		}
	}, [players])

	return (
		<CanvasRenderer
			blockId={!players ? blockId : undefined}
			resolvedBlock={resolvedBlock}
			mode="static"
			viewBox={THUMBNAIL_VIEW_BOX}
			showField={showField}
			scale={THUMBNAIL_SCALE}
			className={className}
		/>
	)
}
